import { hasSuperAdminRole } from './super-admin-access.util';
import { User } from 'app/core/user/user.types';

export interface SicaCatalogRoute {
    path: string;
    isCommon: boolean;
    isSynthetic: boolean;
    guard?: string[];
}

export interface SicaCatalogContext {
    context: string;
    modules: {links: SicaCatalogRoute[]}[];
}

export let SICA_ROUTES: SicaCatalogRoute[] = [];

export function setSicaRouteCatalog(contexts: SicaCatalogContext[]): void {
    if (!Array.isArray(contexts)) throw new Error('Catalogue des routes SICA invalide.');
    SICA_ROUTES = contexts.flatMap(context => context.modules.flatMap(module => module.links));
}

export function isSuperAdminOnlySicaRoute(link: string): boolean {
    return !!link && SICA_ROUTES.some(route => route.path.toLowerCase() == link.toLowerCase() &&
        route.guard?.includes('SuperAdminGuard'));
}

export function isCommonSicaRoute(link: string): boolean {
    return !!link && SICA_ROUTES.some(route => route.isCommon && route.path.toLowerCase() == link.toLowerCase());
}

/** Resolve Angular URL segments to a catalog path; permissions still compare that path exactly. */
export function resolveSicaRoutePath(segments: string[]): string {
    const candidates = SICA_ROUTES.filter(route => {
        if (route.isSynthetic) return false;
        const pattern = route.path.slice(1).split('/');
        return pattern.length === segments.length && pattern.every((part, index) =>
            part.startsWith(':') || part.toLowerCase() == segments[index].toLowerCase()
        );
    });
    candidates.sort((left, right) =>
        left.path.split('/').filter(part => part.startsWith(':')).length -
        right.path.split('/').filter(part => part.startsWith(':')).length
    );
    return candidates[0]?.path || '/' + segments.join('/');
}

/** Selection is a sign-in step; dashboard links remain based on page grants. */
export function requiresPartnerSelection(user: User): boolean {
    const names = ['admin e. partenaire', 'admin espace partenaire', 'superviseur',
        'mandataire', 'mandataire projets', 'mandataire administratif', 'mandataire administratif projets'];
    return [...(user?.userProfiles || []), ...(user?.user_UserProfiles || []).map(item => item.userProfile)]
        .some(profile => names.includes(profile?.name?.toLowerCase()));
}

export function initialSicaRoute(user: User): string {
    return !hasSuperAdminRole() && requiresPartnerSelection(user) ? '/partner-selection' : defaultSicaRoute(user);
}

/** Dashboard links use the same granted-route destination as sign-in. */
export function dashboardSicaRoute(user: User): string {
    return defaultSicaRoute(user);
}

export function defaultSicaRoute(user: User): string {
    if (hasSuperAdminRole()) return '/settings/landing';
    const permissions = (user?.user_UserProfiles || []).flatMap(profile => profile.userProfile?.permissions || []);
    const links = permissions.map(permission => permission.pageRoute);
    for (const preferred of ['/dashboard', '/partners/dashboard']) {
        if (links.some(link => link?.toLowerCase() == preferred)) return preferred;
    }
    const page = SICA_ROUTES.find(route => !route.isSynthetic && !route.path.includes(':') &&
        links.some(link => link?.toLowerCase() == route.path.toLowerCase()));
    return page?.path || '/forbidden-access';
}
